import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Save } from "lucide-react";

const AddExperienceModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    cargo: "",
    tipoEmpleo: "",
    empresa1: "",
    empresa2: "",
    actualmente: false,
    mesInicio: "",
    añoInicio: "",
    mesFin: "",
    añoFin: "",
    ubicacion: "",
    descripcion: "",
    titularPerfil: "",
    contenidoMultimedia: "",
  });

  const [descripcionLength, setDescripcionLength] = useState(0);

  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const años = Array.from({ length: 50 }, (_, i) => 2025 - i);

  const tiposEmpleo = [
    "Tiempo completo",
    "Medio tiempo",
    "Por contrato",
    "Freelance",
    "Prácticas",
    "Voluntariado",
  ];

  const ubicaciones = [
    "Presencial",
    "Remoto",
    "Híbrido",
    "Cali, Colombia",
    "Bogotá, Colombia",
    "Medellín, Colombia",
    "Barranquilla, Colombia",
  ];

  const handleInputChange = (field, value) => {
    if (field === "descripcion") {
      setDescripcionLength(value.length);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      actualmente: checked,
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
    setFormData({
      cargo: "",
      tipoEmpleo: "",
      empresa1: "",
      empresa2: "",
      actualmente: false,
      mesInicio: "",
      añoInicio: "",
      mesFin: "",
      añoFin: "",
      ubicacion: "",
      descripcion: "",
      titularPerfil: "",
      contenidoMultimedia: "",
    });
    setDescripcionLength(0);
    onClose();
  };

  const handleUpload = () => {
    alert("Funcionalidad de upload próximamente");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Blurry Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
          >
            {/* Modal: full-width bottom sheet on mobile, centered card on desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-lg sm:max-h-[90vh]"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    AÑADIR EXPERIENCIA
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    * El asterisco indica que es obligatorio.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-700" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-4">
                {/* Cargo */}
                <div>
                  <input
                    type="text"
                    placeholder="Cargo*"
                    value={formData.cargo}
                    onChange={(e) => handleInputChange("cargo", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                </div>

                {/* Tipo de empleo */}
                <div>
                  <div className="relative">
                    <select
                      value={formData.tipoEmpleo}
                      onChange={(e) =>
                        handleInputChange("tipoEmpleo", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                    >
                      <option value="">Tipo de empleo*</option>
                      {tiposEmpleo.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Empresa u organización (1) */}
                <div>
                  <input
                    type="text"
                    placeholder="Empresa u organización*"
                    value={formData.empresa1}
                    onChange={(e) =>
                      handleInputChange("empresa1", e.target.value)
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                </div>

                {/* Empresa u organización (2) */}
                <div>
                  <input
                    type="text"
                    placeholder="Empresa u organización*"
                    value={formData.empresa2}
                    onChange={(e) =>
                      handleInputChange("empresa2", e.target.value)
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                </div>

                {/* Checkbox - Actualmente tengo este cargo */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="actualmente"
                    checked={formData.actualmente}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    className="w-4 h-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="actualmente"
                    className="text-sm text-gray-700"
                  >
                    Actualmente tengo este cargo.
                  </label>
                </div>

                {/* Fecha de inicio */}
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    Fecha de inicio*
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <select
                        value={formData.mesInicio}
                        onChange={(e) =>
                          handleInputChange("mesInicio", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                      >
                        <option value="">Mes</option>
                        {meses.map((mes) => (
                          <option key={mes} value={mes}>
                            {mes}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="relative">
                      <select
                        value={formData.añoInicio}
                        onChange={(e) =>
                          handleInputChange("añoInicio", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                      >
                        <option value="">Año</option>
                        {años.map((año) => (
                          <option key={año} value={año}>
                            {año}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fecha de finalización */}
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    Fecha de finalización*
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <select
                        value={formData.mesFin}
                        onChange={(e) =>
                          handleInputChange("mesFin", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                      >
                        <option value="">Mes</option>
                        {meses.map((mes) => (
                          <option key={mes} value={mes}>
                            {mes}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="relative">
                      <select
                        value={formData.añoFin}
                        onChange={(e) =>
                          handleInputChange("añoFin", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                      >
                        <option value="">Año</option>
                        {años.map((año) => (
                          <option key={año} value={año}>
                            {año}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ubicación */}
                <div>
                  <div className="relative">
                    <select
                      value={formData.ubicacion}
                      onChange={(e) =>
                        handleInputChange("ubicacion", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                    >
                      <option value="">Ubicación*</option>
                      {ubicaciones.map((ubicacion) => (
                        <option key={ubicacion} value={ubicacion}>
                          {ubicacion}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Descripción */}
                <div>
                  <textarea
                    placeholder="Descripción*"
                    value={formData.descripcion}
                    onChange={(e) =>
                      handleInputChange("descripcion", e.target.value)
                    }
                    maxLength={2000}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400 resize-none"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-400">
                      {descripcionLength}/2000
                    </span>
                  </div>
                </div>

                {/* Titular del perfil */}
                <div>
                  <input
                    type="text"
                    placeholder="Titular del perfil*"
                    value={formData.titularPerfil}
                    onChange={(e) =>
                      handleInputChange("titularPerfil", e.target.value)
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                </div>

                {/* Contenido multimedia */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Contenido multimedia"
                    value={formData.contenidoMultimedia}
                    onChange={(e) =>
                      handleInputChange("contenidoMultimedia", e.target.value)
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                  <button
                    onClick={handleUpload}
                    className="px-6 py-2 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
                  >
                    <Upload size={18} />
                    UPLOAD
                  </button>
                </div>
              </div>

              {/* Footer - Save Button */}
              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-8 py-3 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
                >
                  <Save size={18} />
                  GUARDAR
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddExperienceModal;
